import { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, Mail, Lock, UserCircle, Shield, Eye, EyeOff, Handshake } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userType, setUserType] = useState('employee'); // 'employee', 'admin', 'associe'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('[LOGIN DEBUG] Type sélectionné:', userType);
    console.log('[LOGIN DEBUG] Email:', email);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      const role = response.data.user?.role;
      console.log('[LOGIN DEBUG] Connexion réussie - rôle reçu:', role);

      // Vérifier que le type sélectionné correspond au rôle réel du compte
      const allowedRoles = {
        admin: ['admin'],
        employee: ['employee', 'employer', 'technicien'],
        associe: ['associe', 'investisseur'],
      };
      const allowed = allowedRoles[userType] || [];
      if (!allowed.includes(role)) {
        const expected =
          userType === 'admin' ? 'administrateur'
          : userType === 'employee' ? 'employé / technicien'
          : 'investisseur';
        const actual =
          role === 'admin' ? 'administrateur'
          : role === 'employee' || role === 'employer' ? 'employé'
          : role === 'technicien' ? 'technicien'
          : role === 'associe' || role === 'investisseur' ? 'investisseur'
          : role;
        setError(`Ce compte est de type ${actual}, mais vous avez sélectionné "${expected}". Cliquez sur l'onglet correspondant à votre compte.`);
        setLoading(false);
        return;
      }

      onLogin(response.data.access_token, response.data.user);
    } catch (err) {
      console.error('[LOGIN DEBUG] ERREUR:', err);
      setError(err.response?.data?.detail || 'Email ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image SESAM */}
      <div className="hidden md:flex md:w-1/2 relative bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_sesam-crm/artifacts/yykvu9pf_11062FAD-C8BF-41B2-9E19-490FD5854A6C.png"
            alt="SESAM Panneaux Publicitaires"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 to-transparent"></div>
        </div>
        
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <h1 className="text-6xl font-bold mb-4 drop-shadow-2xl">
            SESAM
          </h1>
          <p className="text-2xl mb-2 drop-shadow-lg font-semibold">
            Système de Gestion CRM
          </p>
          <p className="text-lg text-blue-100 drop-shadow-md">
            Solution complète pour la publicité et la communication visuelle
          </p>
          <div className="mt-8 space-y-3 text-blue-100">
            <p className="flex items-center gap-2">
              <span className="text-2xl">📊</span>
              <span>Gestion des prospects et clients</span>
            </p>
            <p className="flex items-center gap-2">
              <span className="text-2xl">🎬</span>
              <span>Services audio-visuels</span>
            </p>
            <p className="flex items-center gap-2">
              <span className="text-2xl">📈</span>
              <span>Suivi des contrats et revenus</span>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-gray-50 p-8">
        <div className="w-full max-w-md">
          {/* Logo mobile */}
          <div className="md:hidden text-center mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
              SESAM
            </h1>
            <p className="text-gray-600">Système de Gestion CRM</p>
          </div>

          {/* User Type Selection */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Connexion
            </h2>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <button
                type="button"
                onClick={() => setUserType('employee')}
                data-testid="role-employee-btn"
                className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                  userType === 'employee'
                    ? 'bg-blue-600 border-blue-600 text-white shadow-lg scale-105'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300 hover:shadow-md'
                }`}
              >
                <UserCircle className="h-8 w-8 mx-auto mb-2" />
                <p className="font-semibold text-sm">Je suis employé</p>
                <p className="text-xs mt-1 opacity-80">SESAM</p>
              </button>
              
              <button
                type="button"
                onClick={() => setUserType('admin')}
                data-testid="role-admin-btn"
                className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                  userType === 'admin'
                    ? 'bg-purple-600 border-purple-600 text-white shadow-lg scale-105'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-purple-300 hover:shadow-md'
                }`}
              >
                <Shield className="h-8 w-8 mx-auto mb-2" />
                <p className="font-semibold text-sm">Je suis administrateur</p>
                <p className="text-xs mt-1 opacity-80">SESAM</p>
              </button>

              <button
                type="button"
                onClick={() => setUserType('associe')}
                data-testid="role-associe-btn"
                className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                  userType === 'associe'
                    ? 'bg-amber-600 border-amber-600 text-white shadow-lg scale-105'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-amber-300 hover:shadow-md'
                }`}
              >
                <Handshake className="h-8 w-8 mx-auto mb-2" />
                <p className="font-semibold text-sm">Je suis investisseur</p>
                <p className="text-xs mt-1 opacity-80">SESAM</p>
              </button>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 rounded-2xl shadow-xl">
            {error && (
              <Alert variant="destructive" data-testid="login-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div>
              <Label htmlFor="email" className="text-base font-semibold text-gray-700">
                Adresse email
              </Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="votre.email@sesam.fr"
                  className="pl-10 h-12 text-base"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-base font-semibold text-gray-700">
                Mot de passe
              </Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-12 text-base"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-button"
              className={`w-full h-12 text-base font-semibold transition-all ${
                userType === 'admin'
                  ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg shadow-purple-500/50'
                  : userType === 'associe'
                  ? 'bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 shadow-lg shadow-amber-500/50'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-500/50'
              }`}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Connexion...
                </span>
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 mt-6">
            © 2026 SESAM. Tous droits réservés.
          </p>
        </div>
      </div>
    </div>
  );
}
